// src/components/ChatModal.tsx
import { logger } from '../utils/logger';
// ÃƒÆ’Ã‚Â¢Ãƒâ€¦Ã¢â‚¬Å“ÃƒÂ¢Ã¢â€šÂ¬Ã‚Â¦ UPDATED: Added is_admin prop support
// ÃƒÆ’Ã‚Â°Ãƒâ€¦Ã‚Â¸Ãƒâ€¦Ã‚Â½Ãƒâ€šÃ‚Â¯ FIXED: Keyboard persistence - stays open after sending message

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonButtons,
  IonIcon,
  IonContent,
  IonFooter,
  IonTextarea,
  IonSpinner,
  IonAvatar,
  IonActionSheet,
  IonAlert,
  IonText,
} from '@ionic/react';
import {
  close,
  send,
  checkmarkDone,
  checkmark,
  ellipsisVertical,
  volumeMute,
  notifications,
  warningOutline,
  banOutline,
  imageOutline,
  checkmarkCircle,
  closeCircle,
  add,
} from 'ionicons/icons';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { sendMessage, getMessages, markAllMessagesAsRead, subscribeToMessages, unsubscribeFromMessages, Message } from '../services/chatService';
import { supabase } from '../services/supabase';
import { ReportModal } from './ReportModal';
import { moderateChatMessage } from '../services/contentModeration';
import { muteChat, unmuteChat, isMuted, MuteDuration } from '../services/muteService';
import { isBlockedRelationship } from '../services/blockProtectionService';
import { AvatarPreview } from './avatar/AvatarPreview';
import { UserProfileModal } from './UserProfileModal';
import { MessageReactions } from './MessageReactions';
import {
  getMessageReactions,
  addMessageReaction,
  removeMessageReaction,
  subscribeToReactions,
  ReactionSummary
} from '../services/reactionsService';
import { uploadFileWithRetry } from '../services/retryService';
import { handleError, ErrorMessages } from '../services/errorHandler';
import {
  getMatchImageConsent,
  requestMatchImageSharing,
  approveMatchImageSharing,
  rejectMatchImageSharing,
  disableMatchImageSharing,
  ImageConsentStatus
} from '../services/imageConsentService';
import { themeService } from '../services/themeService';
import { MessageList } from './chat/MessageList';
import { ImageConsentBanner } from './chat/ImageConsentBanner';
import './ChatModal.css';

// Rainbow colors moved inside component as useMemo for performance

interface ChatModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  matchId: string;
  otherUser: {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string | null;
    avatar?: any;
    is_admin?: boolean;
  };
}

export const ChatModal: React.FC<ChatModalProps> = ({
  isOpen,
  onDismiss,
  matchId,
  otherUser,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [isTyping, setIsTyping] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showMuteAlert, setShowMuteAlert] = useState(false);
  const [isChatMuted, setIsChatMuted] = useState(false);
  const [mutedUntil, setMutedUntil] = useState<string | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockAlert, setShowBlockAlert] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [showContentAlert, setShowContentAlert] = useState(false);
  const [contentAlertMessage, setContentAlertMessage] = useState('');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [imageConsentStatus, setImageConsentStatus] = useState<ImageConsentStatus | null>(null);
  const [loadingImageConsent, setLoadingImageConsent] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null); // Changed to store base64 string
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [messageReactions, setMessageReactions] = useState<{ [messageId: string]: ReactionSummary[] }>({});
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const [isPrideTheme, setIsPrideTheme] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(document.body.classList.contains('dark'));
  const [screenReaderAnnouncement, setScreenReaderAnnouncement] = useState('');
  const contentRef = useRef<HTMLIonContentElement>(null);
  const textareaRef = useRef<HTMLIonTextareaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const subscriptionRef = useRef<any>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const typingChannelRef = useRef<any>(null);

  // Memoize rainbow colors based on dark mode to avoid DOM queries on every render
  const rainbowColors = useMemo(() => [
    { bg: isDarkMode ? '#ef4444' : '#dc2626', text: '#ffffff' }, // red (primary)
    { bg: isDarkMode ? '#f97316' : '#ea580c', text: isDarkMode ? '#ffffff' : '#000000' }, // orange (quaternary)
    { bg: isDarkMode ? '#eab308' : '#ca8a04', text: '#000000' }, // yellow (secondary)
    { bg: isDarkMode ? '#22c55e' : '#16a34a', text: isDarkMode ? '#000000' : '#ffffff' }, // green (quinary)
    { bg: isDarkMode ? '#3b82f6' : '#2563eb', text: '#ffffff' }, // blue (senary)
    { bg: isDarkMode ? '#a855f7' : '#9333ea', text: '#ffffff' }, // purple (tertiary)
  ], [isDarkMode]);

  const getMessageColor = (index: number) => rainbowColors[index % rainbowColors.length];

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      if (isOpen) {
        setLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (user && mounted) {
          setCurrentUserId(user.id);
        }

        // Check mute status
        const muteStatus = await isMuted(matchId, 'match');
        if (muteStatus.success && mounted) {
          setIsChatMuted(muteStatus.muted);
          setMutedUntil(muteStatus.mutedUntil || null);
        }

        // Check if user is blocked
        const blockStatus = await isBlockedRelationship(otherUser.id);
        if (mounted) {
          setIsBlocked(blockStatus.blocked);
        }

        // Load image consent status
        await loadImageConsentStatus();

        await loadMessages();
        if (mounted) {
          setupSubscription();
          setupTypingChannel();
        }
      }
    };

    init();

    return () => {
      mounted = false;
      cleanup();
    };
  }, [isOpen, matchId]);

  // Check if pride theme is active for rainbow cycling
  useEffect(() => {
    const checkTheme = () => {
      const currentPreset = themeService.getThemePreset();
      setIsPrideTheme(currentPreset === 'pride');
    };

    checkTheme();
    // Check theme when modal opens
    if (isOpen) {
      checkTheme();
    }
  }, [isOpen]);

  // Watch for dark mode changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains('dark'));
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // Focus management: Focus textarea when modal opens and loading completes
  useEffect(() => {
    if (isOpen && !loading && !isBlocked) {
      setTimeout(() => {
        textareaRef.current?.setFocus();
      }, 300); // Delay to allow modal animation to complete
    }
  }, [isOpen, loading, isBlocked]);

  const cleanup = () => {
    if (subscriptionRef.current) {
      unsubscribeFromMessages(subscriptionRef.current);
      subscriptionRef.current = null;
    }
    if (typingChannelRef.current) {
      supabase.removeChannel(typingChannelRef.current);
      typingChannelRef.current = null;
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  };

  const loadImageConsentStatus = async () => {
    const result = await getMatchImageConsent(matchId);
    if (result.success && result.status) {
      setImageConsentStatus(result.status);
    }
  };

  const handleRequestImageSharing = async () => {
    setLoadingImageConsent(true);
    const result = await requestMatchImageSharing(matchId);
    if (result.success) {
      await loadImageConsentStatus();
    } else {
      alert(result.error || 'Failed to request image sharing');
    }
    setLoadingImageConsent(false);
  };

  const handleApproveImageSharing = async () => {
    setLoadingImageConsent(true);
    const result = await approveMatchImageSharing(matchId);
    if (result.success) {
      await loadImageConsentStatus();
    } else {
      alert(result.error || 'Failed to approve image sharing');
    }
    setLoadingImageConsent(false);
  };

  const handleRejectImageSharing = async () => {
    setLoadingImageConsent(true);
    const result = await rejectMatchImageSharing(matchId);
    if (result.success) {
      await loadImageConsentStatus();
    } else {
      alert(result.error || 'Failed to reject image sharing');
    }
    setLoadingImageConsent(false);
  };

  const handleDisableImageSharing = async () => {
    setShowActionSheet(false);
    const confirmed = window.confirm('Are you sure you want to disable image sharing? Both users will need to consent again to re-enable it.');
    if (!confirmed) return;

    setLoadingImageConsent(true);
    const result = await disableMatchImageSharing(matchId);
    if (result.success) {
      await loadImageConsentStatus();
    } else {
      alert(result.error || 'Failed to disable image sharing');
    }
    setLoadingImageConsent(false);
  };

  const loadMessages = async () => {
    const result = await getMessages(matchId);
    if (result.success && result.messages) {
      setMessages(result.messages);
      setHasMoreMessages(result.hasMore ?? false);
      await markAllMessagesAsRead(matchId);
      scrollToBottom();
      loadReactionsForMessages(result.messages);
    }
    setLoading(false);
  };

  const loadOlderMessages = async () => {
    if (loadingMore || !hasMoreMessages || messages.length === 0) return;
    setLoadingMore(true);

    const oldestTimestamp = messages[0].created_at;
    const result = await getMessages(matchId, oldestTimestamp);

    if (result.success && result.messages && result.messages.length > 0) {
      setMessages(prev => [...result.messages!, ...prev]);
      setHasMoreMessages(result.hasMore ?? false);
      loadReactionsForMessages(result.messages);
    }
    setLoadingMore(false);
  };

  const loadReactionsForMessages = async (msgs: Message[]) => {
    // Load all reactions in parallel for better performance
    const results = await Promise.all(
      msgs.map(msg => getMessageReactions(msg.id))
    );

    const reactionsMap: { [messageId: string]: ReactionSummary[] } = {};
    msgs.forEach((msg, index) => {
      const result = results[index];
      if (result.success && result.data) {
        reactionsMap[msg.id] = result.data;
      }
    });

    setMessageReactions(reactionsMap);
  };

  const handleAddReaction = async (messageId: string, emoji: string) => {
    const result = await addMessageReaction(messageId, emoji);
    if (result.success) {
      // Reload reactions for this message
      const reactionsResult = await getMessageReactions(messageId);
      if (reactionsResult.success && reactionsResult.data) {
        setMessageReactions(prev => ({
          ...prev,
          [messageId]: reactionsResult.data!
        }));
      }
    }
  };

  const handleRemoveReaction = async (messageId: string, emoji: string) => {
    const result = await removeMessageReaction(messageId, emoji);
    if (result.success) {
      // Reload reactions for this message
      const reactionsResult = await getMessageReactions(messageId);
      if (reactionsResult.success && reactionsResult.data) {
        setMessageReactions(prev => ({
          ...prev,
          [messageId]: reactionsResult.data!
        }));
      }
    }
  };

  const setupSubscription = () => {
    subscriptionRef.current = subscribeToMessages(matchId, (message) => {
      setMessages(prev => [...prev, message]);
      markAllMessagesAsRead(matchId);
      scrollToBottom();

      // Announce new message to screen readers
      if (message.sender_id !== currentUserId) {
        const senderName = otherUser.display_name || otherUser.username;
        setScreenReaderAnnouncement(`New message from ${senderName}: ${message.content}`);
      }
    });
  };

  const setupTypingChannel = () => {
    const channel = supabase.channel(`typing:${matchId}`);

    channel
      .on('broadcast', { event: 'typing' }, (payload) => {
        if (payload.payload.userId !== currentUserId) {
          setOtherUserTyping(true);
          setTimeout(() => setOtherUserTyping(false), 3000);
        }
      })
      .subscribe();

    typingChannelRef.current = channel;
  };

  const handleTyping = useCallback(() => {
    if (!isTyping) {
      setIsTyping(true);
      typingChannelRef.current?.send({
        type: 'broadcast',
        event: 'typing',
        payload: { userId: currentUserId }
      });
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 2000);
  }, [isTyping, currentUserId]);

  const scrollToBottom = () => {
    setTimeout(() => {
      contentRef.current?.scrollToBottom(300);
    }, 100);
  };

  const applyImageDataUrl = (dataUrl: string) => {
    const approximateSize = dataUrl.length * 0.75;
    if (approximateSize > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }
    setSelectedImage(dataUrl);
    setImagePreview(dataUrl);
  };

  // Web fallback: read file selected via <input type="file">
  const handleWebFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      if (dataUrl) applyImageDataUrl(dataUrl);
    };
    reader.readAsDataURL(file);
    e.target.value = ''; // allow re-selecting the same file
  };

  const handleImageSelect = async () => {
    if (!Capacitor.isNativePlatform()) {
      fileInputRef.current?.click();
      return;
    }

    try {
      logger.log('📱 Opening photo picker...');
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,
        promptLabelHeader: 'Select Photo',
        promptLabelPhoto: 'From Photos',
      });

      if (image.dataUrl) {
        applyImageDataUrl(image.dataUrl);
        logger.log('✅ Image set successfully');
      }
    } catch (error: any) {
      if (error.message && !error.message.includes('User cancelled')) {
        alert(`Failed to select image: ${error.message}`);
      }
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleAddImageClick = () => {
    handleImageSelect();
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && !selectedImage) || sending) return;

    // Check if user is blocked
    if (isBlocked) {
      alert('You cannot send messages to this user.');
      return;
    }

    // Content moderation check (only for text messages)
    const messageText = newMessage.trim();
    if (messageText) {
      const moderationResult = moderateChatMessage(messageText);

      if (!moderationResult.isClean) {
        setContentAlertMessage(moderationResult.reason || 'This message contains inappropriate content');
        setShowContentAlert(true);
        return;
      }
    }

    setSending(true);

    try {
      let imageUrl: string | null = null;

      // Upload image if selected
      if (selectedImage) {
        // Convert base64 data URL to Blob without fetch (CSP-compliant)
        const base64Data = selectedImage.split(',')[1];
        const mimeType = selectedImage.split(',')[0].match(/:(.*?);/)?.[1] || 'image/jpeg';

        // Decode base64 to binary
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Create blob
        const blob = new Blob([bytes], { type: mimeType });

        // Determine file extension from MIME type
        const fileExt = mimeType.split('/')[1] || 'jpg';
        const fileName = `${matchId}/${Date.now()}.${fileExt}`;

        // Upload with retry logic
        const uploadResult = await uploadFileWithRetry(
          'chat-images',
          fileName,
          blob,
          {
            cacheControl: '3600',
            upsert: false,
            contentType: mimeType
          }
        );

        if (!uploadResult.success) {
          logger.error('Error uploading image:', uploadResult.error);
          const errorMsg = uploadResult.error?.userMessage || ErrorMessages.IMAGE_UPLOAD_FAILED;
          alert(errorMsg);
          setSending(false);
          return;
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('chat-images')
          .getPublicUrl(fileName);

        imageUrl = publicUrl;
      }

      // Send message (with or without image)
      const result = await sendMessage(matchId, messageText || '[Image]', imageUrl || undefined);

      if (result.success) {
        setNewMessage('');
        handleRemoveImage();
        scrollToBottom();

        // CRITICAL: Keep keyboard open and refocus textarea
        setTimeout(() => {
          textareaRef.current?.setFocus();
        }, 50);
      } else {
        const appError = handleError(result.error || new Error('Failed to send message'), 'send message');
        alert(appError.userMessage);
      }
    } catch (error) {
      logger.error('Error sending message:', error);
      const appError = handleError(error, 'send message');
      alert(appError.userMessage);
    }

    setSending(false);
  };
  const handleKeyPress = (e: any) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTextareaFocus = async () => {
    try {
      await Keyboard.show();
    } catch (error) {
      // Keyboard plugin may not be available on web
    }
    setTimeout(() => scrollToBottom(), 300);
  };

  const handleContentClick = () => {
    try {
      Keyboard.hide();
    } catch (error) {
      // Keyboard plugin may not be available on web
    }
  };

  const handleMute = async (duration: MuteDuration) => {
    const result = await muteChat(matchId, 'match', duration);
    if (result.success) {
      setIsChatMuted(true);
      // Note: mutedUntil is not returned from muteChat, so we'll just set it to null for now
      // or refetch the mute status if needed
      const muteStatus = await isMuted(matchId, 'match');
      if (muteStatus.success) {
        setMutedUntil(muteStatus.mutedUntil || null);
      }
    }
    setShowMuteAlert(false);
  };

  const handleUnmute = async () => {
    const result = await unmuteChat(matchId, 'match');
    if (result.success) {
      setIsChatMuted(false);
      setMutedUntil(null);
    }
  };

  const handleBlock = async () => {
    try {
      // Block the user
      const { error } = await supabase
        .from('blocked_users')
        .insert({
          blocker_id: currentUserId,
          blocked_id: otherUser.id,
        });

      if (error) throw error;

      setIsBlocked(true); // Update blocked state
      onDismiss(); // Close the chat modal
    } catch (error) {
      logger.error('Error blocking user:', error);
    }
  };

  const handleReportComplete = () => {
    setShowReportModal(false);
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleDismiss = () => {
    cleanup();
    onDismiss();
  };

  // Memoized event handlers to prevent unnecessary re-renders
  const handleTextareaInput = useCallback((e: any) => {
    setNewMessage(e.detail.value!);
    handleTyping();
  }, [handleTyping]);

  const handleAvatarClick = useCallback(() => {
    setShowProfileModal(true);
  }, []);

  const handleAvatarKeyDown = useCallback((e: any) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setShowProfileModal(true);
    }
  }, []);

  const handleActionSheetClick = useCallback(() => {
    setShowActionSheet(true);
  }, []);

  const handleMessageClick = useCallback((messageId: string) => {
    setActiveMessageId(prev => prev === messageId ? null : messageId);
  }, []);

  const handleImageClick = useCallback((e: any, imageUrl: string) => {
    e.stopPropagation();
    window.open(imageUrl, '_blank');
  }, []);

  return (
    <>
      {/* Screen reader announcement region */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {screenReaderAnnouncement}
      </div>

      {/* Hidden file input for web image picking (used when not on native platform) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleWebFileChange}
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      <IonModal isOpen={isOpen} onDidDismiss={handleDismiss}>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={handleDismiss} aria-label="Close chat">
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
            <IonTitle style={{ paddingLeft: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IonAvatar
                  style={{ width: '32px', height: '32px', cursor: 'pointer', marginLeft: '48px' }}
                  onClick={handleAvatarClick}
                  onKeyDown={handleAvatarKeyDown}
                  tabIndex={0}
                  role="button"
                  aria-label={`View profile of ${otherUser.display_name || otherUser.username}`}
                >
                  {otherUser.avatar_url ? (
                    <img src={otherUser.avatar_url} alt={otherUser.display_name || otherUser.username} />
                  ) : otherUser.avatar ? (
                    <AvatarPreview
                      avatarData={otherUser.avatar}
                      size={32}
                      userId={otherUser.id}
                      isAdmin={otherUser.is_admin || false}
                    />
                  ) : (
                    <AvatarPreview
                      size={32}
                      userId={otherUser.id}
                      isAdmin={otherUser.is_admin || false}
                    />
                  )}
                </IonAvatar>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{otherUser.display_name || otherUser.username}</span>
                    {isChatMuted && (
                      <IonIcon icon={volumeMute} style={{ fontSize: '16px', color: 'var(--ion-color-medium)' }} aria-label="Chat is muted" />
                    )}
                  </div>
                  {otherUserTyping && (
                    <span style={{ fontSize: '12px', color: 'var(--ion-color-medium)' }}>
                      typing...
                    </span>
                  )}
                </div>
              </div>
            </IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={handleActionSheetClick} aria-label="Open chat options">
                <IonIcon icon={ellipsisVertical} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent ref={contentRef} onClick={handleContentClick}>
          <ImageConsentBanner
            imageConsentStatus={imageConsentStatus}
            otherUserName={otherUser.display_name || otherUser.username}
            loadingImageConsent={loadingImageConsent}
            onApprove={handleApproveImageSharing}
            onReject={handleRejectImageSharing}
            onRequest={handleRequestImageSharing}
          />

          {hasMoreMessages && (
            <div style={{ textAlign: 'center', padding: '8px' }}>
              <IonButton
                fill="clear"
                size="small"
                disabled={loadingMore}
                onClick={loadOlderMessages}
              >
                {loadingMore ? <IonSpinner name="crescent" /> : 'Load older messages'}
              </IonButton>
            </div>
          )}

          <MessageList
            messages={messages}
            loading={loading}
            currentUserId={currentUserId}
            otherUserName={otherUser.display_name || otherUser.username}
            otherUserId={otherUser.id}
            matchId={matchId}
            isPrideTheme={isPrideTheme}
            activeMessageId={activeMessageId}
            messageReactions={messageReactions}
            getMessageColor={getMessageColor}
            formatTime={formatTime}
            onMessageClick={handleMessageClick}
            onImageClick={handleImageClick}
            onAddReaction={handleAddReaction}
            onRemoveReaction={handleRemoveReaction}
          />
        </IonContent>

        <IonFooter>
          {/* Image Preview */}
          {imagePreview && (
            <div style={{
              padding: '12px',
              backgroundColor: 'var(--ion-background-color)',
              borderTop: '1px solid var(--ion-color-light)'
            }}>
              <div style={{
                position: 'relative',
                display: 'inline-block',
              }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    borderRadius: '12px',
                    objectFit: 'cover'
                  }}
                />
                <IonButton
                  fill="solid"
                  color="danger"
                  onClick={handleRemoveImage}
                  aria-label="Remove image"
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    '--border-radius': '50%',
                    '--padding-start': '0',
                    '--padding-end': '0',
                    width: '32px',
                    height: '32px',
                    minWidth: '32px',
                    minHeight: '32px',
                    margin: 0,
                  }}
                >
                  <IonIcon icon={close} style={{ fontSize: '20px' }} />
                </IonButton>
              </div>
            </div>
          )}

          <div className="chat-input-container" style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '8px',
            padding: '12px',
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
            backgroundColor: 'var(--ion-background-color)',
            borderTop: imagePreview ? 'none' : '1px solid var(--ion-border-color)'
          }}>
            {/* Add Image Button (only show if image sharing is enabled) */}
            {imageConsentStatus?.enabled && !imagePreview && (
              <IonButton
                fill="clear"
                onClick={handleAddImageClick}
                disabled={sending || isBlocked}
                aria-label="Add image"
                style={{
                  '--border-radius': '50%',
                  width: '44px',
                  height: '44px',
                  margin: 0,
                  flexShrink: 0,
                  '--color': 'var(--ion-color-medium)'
                }}
              >
                <IonIcon icon={add} />
              </IonButton>
            )}

            <IonTextarea
              ref={textareaRef}
              value={newMessage}
              onIonInput={handleTextareaInput}
              onIonFocus={handleTextareaFocus}
              onKeyDown={handleKeyPress}
              placeholder={isBlocked ? "You cannot message this user" : "Type a message..."}
              autoGrow={true}
              rows={1}
              maxlength={500}
              autocapitalize="sentences"
              disabled={sending || isBlocked}
              style={{
                flex: 1,
                '--background': 'var(--ion-color-step-150, #f3f4f6)',
                '--color': 'var(--ion-text-color)',
                '--padding-start': '12px',
                '--padding-end': '12px',
                '--padding-top': '8px',
                '--padding-bottom': '8px',
                borderRadius: '20px',
                margin: 0
              }}
            />
            <IonButton
              onClick={handleSend}
              disabled={(!newMessage.trim() && !selectedImage) || sending || isBlocked}
              aria-label="Send message"
              style={{
                '--border-radius': '50%',
                width: '44px',
                height: '44px',
                margin: 0,
                flexShrink: 0
              }}
            >
              {sending ? <IonSpinner name="crescent" /> : <IonIcon icon={send} />}
            </IonButton>
          </div>
        </IonFooter>
      </IonModal>

      <IonActionSheet
        isOpen={showActionSheet}
        onDidDismiss={() => setShowActionSheet(false)}
        header="Chat Options"
        buttons={[
          {
            text: isChatMuted ? 'Unmute' : 'Mute',
            icon: isChatMuted ? notifications : volumeMute,
            handler: () => {
              if (isChatMuted) {
                handleUnmute();
              } else {
                setShowMuteAlert(true);
              }
            },
          },
          ...(imageConsentStatus?.enabled ? [{
            text: "Disable Image Sharing",
            icon: imageOutline,
            handler: handleDisableImageSharing,
          }] : []),
          {
            text: "Report User",
            icon: warningOutline,
            role: "destructive",
            handler: () => {
              setShowReportModal(true);
            },
          },
          {
            text: "Block User",
            icon: banOutline,
            role: "destructive",
            handler: () => {
              setShowBlockAlert(true);
            },
          },
          {
            text: 'Cancel',
            role: 'cancel',
          },
        ]}
      />

      <IonAlert
        isOpen={showMuteAlert}
        onDidDismiss={() => setShowMuteAlert(false)}
        header="Mute Notifications"
        message="How long would you like to mute notifications from this chat?"
        buttons={[
          {
            text: '1 hour',
            handler: () => handleMute('1hour'),
          },
          {
            text: '8 hours',
            handler: () => handleMute('8hours'),
          },
          {
            text: '1 week',
            handler: () => handleMute('1week'),
          },
          {
            text: 'Forever',
            handler: () => handleMute('indefinite'),
          },
          {
            text: 'Cancel',
            role: 'cancel',
          },
        ]}
      />
      <ReportModal
        isOpen={showReportModal}
        onDismiss={() => setShowReportModal(false)}
        reportType="user"
        reportedId={otherUser.id}
        reportedName={otherUser.display_name || otherUser.username}
        onReportComplete={handleReportComplete}
      />

      <IonAlert
        isOpen={showBlockAlert}
        onDidDismiss={() => setShowBlockAlert(false)}
        header="Block User"
        message={`Are you sure you want to block ${otherUser.display_name || otherUser.username}? They will not be able to message you anymore and you will not see their profile.`}
        buttons={[
          {
            text: "Cancel",
            role: "cancel",
          },
          {
            text: "Block",
            role: "destructive",
            handler: handleBlock,
          },
        ]}
      />

      <IonAlert
        isOpen={showContentAlert}
        onDidDismiss={() => setShowContentAlert(false)}
        header="Message Not Allowed"
        message={contentAlertMessage}
        buttons={['OK']}
      />

      <UserProfileModal
        isOpen={showProfileModal}
        onDismiss={() => setShowProfileModal(false)}
        userId={otherUser.id}
        currentUserId={currentUserId}
        showChatButton={false}
        onBlock={() => {
          setIsBlocked(true);
          setShowProfileModal(false);
        }}
      />
    </>
  );
};

export default ChatModal;